import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'

interface CuratorContextType {
  isCurator: boolean
  setIsCurator: (v: boolean) => void
  /** true when curator also has a client account */
  isAlsoClient: boolean
  setIsAlsoClient: (v: boolean) => void
  /** true when client also has an active curator account */
  isAlsoCurator: boolean
  setIsAlsoCurator: (v: boolean) => void
  viewingClientId: string | null
  viewingClientName: string | null
  /** true when an admin is viewing a client read-only (via ?admin_token link) */
  isAdminView: boolean
  enterClient: (clientId: string, clientName: string) => Promise<void>
  exitClient: () => void
  /** Switch from curator mode to own client mode (re-login with forceMode) */
  switchToClientMode: () => Promise<void>
  /** Switch from client mode back to curator mode (re-login with forceMode) */
  switchToCuratorMode: () => Promise<void>
}

const CuratorContext = createContext<CuratorContextType>({
  isCurator: false,
  setIsCurator: () => {},
  isAlsoClient: false,
  setIsAlsoClient: () => {},
  isAlsoCurator: false,
  setIsAlsoCurator: () => {},
  viewingClientId: null,
  viewingClientName: null,
  isAdminView: false,
  enterClient: async () => {},
  exitClient: () => {},
  switchToClientMode: async () => { /* noop */ },
  switchToCuratorMode: async () => { /* noop */ },
})

export function CuratorProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [isCurator, setIsCurator] = useState(() => localStorage.getItem('tfa_is_curator') === 'true')
  const [isAlsoClient, setIsAlsoClient] = useState(() => localStorage.getItem('tfa_is_also_client') === 'true')
  const [isAlsoCurator, setIsAlsoCurator] = useState(() => localStorage.getItem('tfa_is_also_curator') === 'true')
  const [viewingClientId, setViewingClientId] = useState<string | null>(() => localStorage.getItem('tfa_viewing_client_id'))
  const [viewingClientName, setViewingClientName] = useState<string | null>(() => localStorage.getItem('tfa_viewing_client_name'))
  const [isAdminView, setIsAdminView] = useState(() => localStorage.getItem('tfa_admin_view') === '1')

  const setIsCuratorPersistent = useCallback((v: boolean) => {
    setIsCurator(v)
    if (v) {
      localStorage.setItem('tfa_is_curator', 'true')
    } else {
      localStorage.removeItem('tfa_is_curator')
    }
  }, [])

  const setIsAlsoClientPersistent = useCallback((v: boolean) => {
    setIsAlsoClient(v)
    if (v) {
      localStorage.setItem('tfa_is_also_client', 'true')
    } else {
      localStorage.removeItem('tfa_is_also_client')
    }
  }, [])

  const setIsAlsoCuratorPersistent = useCallback((v: boolean) => {
    setIsAlsoCurator(v)
    if (v) {
      localStorage.setItem('tfa_is_also_curator', 'true')
    } else {
      localStorage.removeItem('tfa_is_also_curator')
    }
  }, [])

  const enterClient = useCallback(async (clientId: string, clientName: string) => {
    // Save curator token
    const curatorToken = api.getToken()
    if (curatorToken) {
      localStorage.setItem('tfa_curator_token', curatorToken)
    }

    // Get impersonation token
    const result = await api.impersonateClient(clientId)
    api.setToken(result.token)

    // Clear query cache so fresh client data loads
    queryClient.clear()

    setViewingClientId(clientId)
    setViewingClientName(clientName)
    localStorage.setItem('tfa_viewing_client_id', clientId)
    localStorage.setItem('tfa_viewing_client_name', clientName)

    // Update user query with impersonated client data
    queryClient.setQueryData(['user', 'me'], result.user)
  }, [queryClient])

  const exitClient = useCallback(() => {
    // Restore curator token, if we came from curator impersonation.
    const curatorToken = localStorage.getItem('tfa_curator_token')
    if (curatorToken) {
      api.setToken(curatorToken)
      localStorage.removeItem('tfa_curator_token')
    } else if (localStorage.getItem('tfa_admin_view') === '1') {
      // Admin viewing has no underlying session to fall back to — drop the token.
      api.setToken(null)
    }
    localStorage.removeItem('tfa_admin_view')
    localStorage.removeItem('tfa_admin_client_token')
    localStorage.removeItem('tfa_admin_curator_token')
    localStorage.removeItem('tfa_admin_client_id')
    localStorage.removeItem('tfa_admin_client_name')
    setIsAdminView(false)

    // Clear query cache
    queryClient.clear()

    setViewingClientId(null)
    setViewingClientName(null)
    localStorage.removeItem('tfa_viewing_client_id')
    localStorage.removeItem('tfa_viewing_client_name')
  }, [queryClient])

  const switchToClientMode = useCallback(async () => {
    // Admin read-only view: swap to the pre-minted client token instead of
    // re-logging via Telegram initData (which we don't have, and whose dev
    // fallback would log in as the test persona, not the impersonated user).
    if (localStorage.getItem('tfa_admin_view') === '1') {
      const clientToken = localStorage.getItem('tfa_admin_client_token')
      if (!clientToken) return
      api.setToken(clientToken)
      setIsCuratorPersistent(false)
      setIsAlsoCuratorPersistent(true)
      // Restore the client anchor so the read-only banner + client UI return.
      const anchorId = localStorage.getItem('tfa_admin_client_id')
      const anchorName = localStorage.getItem('tfa_admin_client_name')
      if (anchorId) {
        setViewingClientId(anchorId)
        localStorage.setItem('tfa_viewing_client_id', anchorId)
      }
      if (anchorName) {
        setViewingClientName(anchorName)
        localStorage.setItem('tfa_viewing_client_name', anchorName)
      }
      queryClient.clear()
      await queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
      return
    }

    // Mint our own client token server-side (no Telegram initData needed) and
    // get the full client `user` back so we can populate the cache SYNCHRONOUSLY.
    // Works in the browser and in Telegram for any dual user.
    const result = await api.switchMode('client')

    // Clear curator state
    setIsCuratorPersistent(false)
    setIsAlsoCuratorPersistent(true)

    // Clear any impersonation state
    setViewingClientId(null)
    setViewingClientName(null)
    localStorage.removeItem('tfa_viewing_client_id')
    localStorage.removeItem('tfa_viewing_client_name')
    localStorage.removeItem('tfa_curator_token')

    // Заполняем кэш СИНХРОННО из ответа (как до d553433). Async-рефетч
    // /auth/me создавал окно `user=undefined` + рейс реконсайла isCurator,
    // из-за чего после входа в клиента приложение откидывало обратно в куратора.
    queryClient.clear()
    if (result.user) {
      queryClient.setQueryData(['user', 'me'], result.user)
    } else {
      await queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
    }
  }, [queryClient, setIsCuratorPersistent, setIsAlsoCuratorPersistent])

  const switchToCuratorMode = useCallback(async () => {
    // Admin read-only view: swap to the pre-minted curator token (same human).
    if (localStorage.getItem('tfa_admin_view') === '1') {
      const curatorToken = localStorage.getItem('tfa_admin_curator_token')
      if (!curatorToken) return
      api.setToken(curatorToken)
      setIsCuratorPersistent(true)
      setIsAlsoClientPersistent(true)
      // Curator screen renders only when viewingClientId is empty (App.tsx) —
      // clear it (the anchor stays in tfa_admin_client_id for switching back).
      setViewingClientId(null)
      setViewingClientName(null)
      localStorage.removeItem('tfa_viewing_client_id')
      localStorage.removeItem('tfa_viewing_client_name')
      queryClient.clear()
      await queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
      return
    }

    // Mint our own curator token server-side (no Telegram initData needed).
    const result = await api.switchMode('curator')

    // Set curator state
    setIsCuratorPersistent(true)
    setIsAlsoCuratorPersistent(false)
    if (result.isAlsoClient) {
      setIsAlsoClientPersistent(true)
    }

    // Clear any client viewing state
    setViewingClientId(null)
    setViewingClientName(null)
    localStorage.removeItem('tfa_viewing_client_id')
    localStorage.removeItem('tfa_viewing_client_name')
    localStorage.removeItem('tfa_curator_token')

    // Заполняем кэш СИНХРОННО из ответа (симметрично switchToClientMode) —
    // без окна загрузки и рейса реконсайла.
    queryClient.clear()
    if (result.user) {
      queryClient.setQueryData(['user', 'me'], result.user)
    } else {
      await queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
    }
  }, [queryClient, setIsCuratorPersistent, setIsAlsoCuratorPersistent, setIsAlsoClientPersistent])

  return (
    <CuratorContext.Provider value={{
      isCurator,
      setIsCurator: setIsCuratorPersistent,
      isAlsoClient,
      setIsAlsoClient: setIsAlsoClientPersistent,
      isAlsoCurator,
      setIsAlsoCurator: setIsAlsoCuratorPersistent,
      viewingClientId,
      viewingClientName,
      isAdminView,
      enterClient,
      exitClient,
      switchToClientMode,
      switchToCuratorMode,
    }}>
      {children}
    </CuratorContext.Provider>
  )
}

export function useCurator() {
  return useContext(CuratorContext)
}
